// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiExplorerRequest, ApiExplorerResponse, ApiExplorerHistory, HttpMethod } from '../../types/rpc';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import JsonEditor from '../JsonEditor';
import './ApiExplorer.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const ApiExplorer: React.FC = () => {
  const [request, setRequest] = useState<ApiExplorerRequest>({
    method: HttpMethod.GET,
    path: '/v2/console/status',
    headers: {},
    query_params: {},
    body: ''
  });
  const [response, setResponse] = useState<ApiExplorerResponse | null>(null);
  const [history, setHistory] = useState<ApiExplorerHistory[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [headersText, setHeadersText] = useState('');
  const [queryParamsText, setQueryParamsText] = useState('');

  const sessionRole = authService.getSessionRole();

  useEffect(() => {
    // Load history from localStorage
    const savedHistory = localStorage.getItem('api-explorer-history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to parse API Explorer history:', e);
      }
    }
  }, []);

  const saveToHistory = (req: ApiExplorerRequest, res: ApiExplorerResponse) => {
    const historyItem: ApiExplorerHistory = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      request: req,
      response: res,
      name: `${req.method} ${req.path}`
    };

    const newHistory = [historyItem, ...history.slice(0, 49)]; // Keep last 50 items
    setHistory(newHistory);
    localStorage.setItem('api-explorer-history', JSON.stringify(newHistory));
  };

  const handleSendRequest = async () => {
    if (!canMakeRequest()) {
      setError('You need developer permissions to use the API Explorer');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Parse headers and query params
      const headers: Record<string, string> = {};
      if (headersText.trim()) {
        headersText.split('\n').forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) {
            headers[key] = value;
          }
        });
      }

      const queryParams: Record<string, string> = {};
      if (queryParamsText.trim()) {
        queryParamsText.split('\n').forEach(line => {
          const [key, value] = line.split('=').map(s => s.trim());
          if (key && value) {
            queryParams[key] = value;
          }
        });
      }

      const requestToSend: ApiExplorerRequest = {
        ...request,
        headers,
        query_params: queryParams
      };

      const result = await consoleService.makeApiRequest(requestToSend);
      setResponse(result);
      saveToHistory(requestToSend, result);
    } catch (err: any) {
      setError(err.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFromHistory = (historyItem: ApiExplorerHistory) => {
    setRequest(historyItem.request);
    setResponse(historyItem.response || null);
    
    // Convert headers and query params back to text
    const headersArray = Object.entries(historyItem.request.headers || {})
      .map(([key, value]) => `${key}: ${value}`);
    setHeadersText(headersArray.join('\n'));

    const queryParamsArray = Object.entries(historyItem.request.query_params || {})
      .map(([key, value]) => `${key}=${value}`);
    setQueryParamsText(queryParamsArray.join('\n'));
  };

  const handleClearHistory = () => {
    setHistory([]);
    localStorage.removeItem('api-explorer-history');
  };

  const canMakeRequest = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'info';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'danger';
    return 'secondary';
  };

  const formatJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const commonEndpoints = [
    { method: 'GET', path: '/v2/console/status', description: 'Server Status' },
    { method: 'GET', path: '/v2/console/account', description: 'List Accounts' },
    { method: 'GET', path: '/v2/console/storage', description: 'List Storage Objects' },
    { method: 'GET', path: '/v2/console/runtime', description: 'List RPC Functions' },
    { method: 'POST', path: '/v2/console/runtime/{id}', description: 'Call RPC Function' },
    { method: 'GET', path: '/v2/console/config', description: 'Get Configuration' },
  ];

  return (
    <div className="api-explorer-container">
      <div className="api-explorer-header">
        <h2 className="api-explorer-title">API Explorer</h2>
        <h6 className="api-explorer-subtitle">Test Nakama Console API endpoints</h6>
      </div>

      <div className="api-explorer-content">
        <div className="request-panel">
          <div className="panel-header">
            <h4>Request</h4>
          </div>

          {!canMakeRequest() && (
            <div className="alert alert-warning">
              You need developer permissions to use the API Explorer.
            </div>
          )}

          <div className="request-form">
            <div className="request-line">
              <select
                className="method-select"
                value={request.method}
                onChange={(e) => setRequest(prev => ({ ...prev, method: e.target.value as HttpMethod }))}
                disabled={!canMakeRequest()}
              >
                {Object.values(HttpMethod).map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
              <input
                type="text"
                className="path-input"
                value={request.path}
                onChange={(e) => setRequest(prev => ({ ...prev, path: e.target.value }))}
                placeholder="/v2/console/status"
                disabled={!canMakeRequest()}
              />
              <button
                type="button"
                className="btn btn-primary send-btn"
                onClick={handleSendRequest}
                disabled={!canMakeRequest() || loading || !request.path}
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    <span className="ms-2">Sending...</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>

            <div className="common-endpoints">
              <label>Common Endpoints:</label>
              <div className="endpoints-list">
                {commonEndpoints.map((endpoint, index) => (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-sm btn-outline-secondary endpoint-btn"
                    onClick={() => setRequest(prev => ({
                      ...prev,
                      method: endpoint.method as HttpMethod,
                      path: endpoint.path
                    }))}
                    disabled={!canMakeRequest()}
                    title={endpoint.description}
                  >
                    {endpoint.method} {endpoint.path}
                  </button>
                ))}
              </div>
            </div>

            <div className="request-details">
              <div className="form-group">
                <label htmlFor="headers">Headers (one per line, format: key: value)</label>
                <textarea
                  id="headers"
                  className="form-control"
                  value={headersText}
                  onChange={(e) => setHeadersText(e.target.value)}
                  placeholder="Content-Type: application/json&#10;X-Custom-Header: value"
                  rows={3}
                  disabled={!canMakeRequest()}
                />
              </div>

              <div className="form-group">
                <label htmlFor="query-params">Query Parameters (one per line, format: key=value)</label>
                <textarea
                  id="query-params"
                  className="form-control"
                  value={queryParamsText}
                  onChange={(e) => setQueryParamsText(e.target.value)}
                  placeholder="filter=test&#10;limit=10"
                  rows={3}
                  disabled={!canMakeRequest()}
                />
              </div>

              {['POST', 'PUT', 'PATCH'].includes(request.method) && (
                <div className="form-group">
                  <JsonEditor
                    value={request.body}
                    onChange={(value) => setRequest(prev => ({ ...prev, body: value }))}
                    placeholder='{"key": "value"}'
                    rows={6}
                    disabled={!canMakeRequest()}
                    label="Request Body"
                  />
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              <RedTriangleIcon />
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <div className="response-panel">
          <div className="panel-header">
            <h4>Response</h4>
          </div>

          {response ? (
            <div className="response-content">
              <div className="response-status">
                <span className={`status-badge status-${getStatusColor(response.status)}`}>
                  {response.status} {response.status_text}
                </span>
                <span className="response-time">{response.duration}ms</span>
              </div>

              <div className="response-headers">
                <h6>Headers</h6>
                <pre className="headers-content">
                  {Object.entries(response.headers)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('\n') || 'No headers'}
                </pre>
              </div>

              <div className="response-body">
                <h6>Body</h6>
                <pre className="body-content">
                  {formatJson(response.body)}
                </pre>
              </div>
            </div>
          ) : (
            <div className="no-response">
              No response yet. Send a request to see the response here.
            </div>
          )}
        </div>

        <div className="history-panel">
          <div className="panel-header">
            <h4>History</h4>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleClearHistory}
              disabled={history.length === 0}
            >
              Clear
            </button>
          </div>

          <div className="history-list">
            {history.length === 0 ? (
              <div className="no-history">
                No request history yet.
              </div>
            ) : (
              history.map((item) => (
                <div
                  key={item.id}
                  className="history-item"
                  onClick={() => handleLoadFromHistory(item)}
                >
                  <div className="history-method">{item.request.method}</div>
                  <div className="history-path">{item.request.path}</div>
                  <div className="history-time">
                    {new Date(item.timestamp).toLocaleString()}
                  </div>
                  {item.response && (
                    <div className={`history-status status-${getStatusColor(item.response.status)}`}>
                      {item.response.status}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiExplorer;
